/**
 * The pickup, on screen.
 *
 * Three pieces, sharing the `at_destination` teal because that is the phase this is all about:
 * the card that replaces the hero while David is coordinating, the block that sits inside the
 * hero card on Eben's side, and the floating pill that gets him in and out.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { RefreshButton } from "./RefreshButton";
import { PickupMessages } from "./PickupCommunication";
import { HoldButton, HOLD_DESTRUCTIVE_MS } from "./HoldButton";
import {
	canConfirmPickup,
	deltaText,
	freshnessNote,
	freshnessOf,
	likelyTrainId,
	OFFER_GRACE_MS,
	PICKUP_RIDER_STEPS,
	pickupCard,
	pickupDeck,
	pickupTimingFor,
	riderPickupLine,
	riderStepIndex,
	TRAIN_STANDING_TONES,
	trainStanding,
	trainStandingLabel,
	trainRelativeText,
} from "../../core/pickup";
import type { PickupState, PickupTrain } from "../../shared/types";
import { homeForStation } from "../../shared/origins";
import { clockParts, stationLabel } from "../../core/format";
import { type NavTarget } from "../navigation";
import { Clock } from "./Clock";
import { NavigateLinks } from "./NavigateLinks";

/** Out the door, matching the allowance the morning already uses. */
const PREP_SECONDS = 5 * 60;

/**
 * The pickup's own tones, said in the card's vocabulary.
 *
 * The card only knows good/warn/bad/info, and those already mean urgency here — so "go" is the
 * amber one and everything settled is green.
 */
const CARD_TONE: Record<string, string> = { wait: "info", go: "warn", drive: "info", here: "good", done: "good" };

function timingFor(pickup: PickupState, nowMs: number, train: PickupTrain = pickup.train) {
	return pickupTimingFor(pickup, PREP_SECONDS, nowMs, train);
}

interface PickupCardProps {
	pickup: PickupState;
	timezone: string;
	nowMs: number;
	onChoose: (planId: string) => void;
}

/**
 * David's deck, in place of the hero.
 *
 * One card per train he might be meeting: the one before the likely departure, the likely one,
 * and the two after. Which train Eben actually takes is not settled until he boards, and each of
 * these answers "when do I leave" differently — so the deck is the question, not decoration.
 *
 * Once Eben pins a train the choosing is over and the deck collapses to his.
 */
export function PickupCard({ pickup, timezone, nowMs, onChoose }: PickupCardProps) {
	const scrollerRef = useRef<HTMLDivElement>(null);
	const settleRef = useRef<number | undefined>(undefined);
	const programmaticRef = useRef(false);
	/*
	 * A choice has to come from a thumb.
	 *
	 * Centring the deck on the likely train is itself a scroll, and the 120 ms guard against it
	 * races the 90 ms settle — so without this the app can record a swipe nobody made and then plan
	 * against it. Only a real touch arms the settle.
	 */
	const touchedRef = useRef(false);

	const pinnedId = pickup.stage === "pinned" ? (pickup.train.planId ?? pickup.likelyPlanId) : null;
	// Worked out here rather than taken from the record: the stored answer was true when Eben's
	// phone last spoke, and sitting in the car for four minutes can turn a likely train into a
	// missed one. The deck slides forward on its own as that happens.
	const likelyId = pinnedId ?? likelyTrainId(pickup.candidates, pickup.riderMetersToStation, nowMs);
	const deck = pickupDeck(pickup.candidates, likelyId, pinnedId);
	const selectedId = pickup.chosenPlanId ?? likelyId;
	const selectedIndex = Math.max(
		0,
		deck.findIndex((train) => train.planId === selectedId),
	);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		const card = scroller?.children[selectedIndex] as HTMLElement | undefined;
		if (!scroller || !card || Math.abs(scroller.scrollLeft - card.offsetLeft) < 4) {
			return;
		}
		programmaticRef.current = true;
		scroller.scrollTo({ left: card.offsetLeft, behavior: "auto" });
		window.setTimeout(() => {
			programmaticRef.current = false;
		}, 120);
	}, [selectedIndex, deck.length]);

	// The same native scroll-snap arbitration the train deck uses: nothing calls preventDefault,
	// so a horizontal swipe and a vertical scroll never fight.
	const handleScroll = useCallback(() => {
		if (programmaticRef.current || !touchedRef.current) {
			return;
		}
		window.clearTimeout(settleRef.current);
		settleRef.current = window.setTimeout(() => {
			const scroller = scrollerRef.current;
			if (!scroller) {
				return;
			}
			const cards = [...scroller.children] as HTMLElement[];
			const centre = scroller.scrollLeft + scroller.clientWidth / 2;
			const nearest = cards.reduce(
				(best, card, index) => {
					const distance = Math.abs(card.offsetLeft + card.clientWidth / 2 - centre);
					return distance < best.distance ? { index, distance } : best;
				},
				{ index: 0, distance: Number.POSITIVE_INFINITY },
			);
			const train = deck[nearest.index];
			if (train?.planId && train.planId !== selectedId) {
				onChoose(train.planId);
			}
		}, 90);
	}, [deck, selectedId, onChoose]);

	useEffect(() => () => window.clearTimeout(settleRef.current), []);

	return (
		<div className={`deck ${deck.length <= 1 ? "deck--single" : ""}`}>
			<div
				className="deck__scroller"
				ref={scrollerRef}
				onScroll={handleScroll}
				onPointerDown={() => {
					touchedRef.current = true;
				}}
			>
				{deck.map((train) => (
					<PickupTrainCard
						key={train.planId ?? "train"}
						pickup={pickup}
						train={train}
						likelyId={likelyId}
						timezone={timezone}
						nowMs={nowMs}
					/>
				))}
			</div>
			{deck.length > 1 ? (
				<div className="deck__dots" aria-hidden="true">
					{deck.map((train, index) => (
						<span
							key={train.planId ?? index}
							className={[
								"deck__dot",
								index === selectedIndex ? "is-active" : "",
								train.planId === likelyId ? "is-recommended" : "",
							].join(" ")}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

function PickupTrainCard({
	pickup,
	train,
	likelyId,
	timezone,
	nowMs,
}: {
	pickup: PickupState;
	train: PickupTrain;
	likelyId: string | null;
	timezone: string;
	nowMs: number;
}) {
	const timing = timingFor(pickup, nowMs, train);
	const trainAt = train.arrivalMs == null ? null : clockParts(train.arrivalMs, timezone).time;
	const copy = pickupCard(pickup.driverProgress, timing, nowMs, trainAt);
	const where = pickup.stationName ? stationLabel(pickup.stationName) : "the station";
	const pinned = pickup.stage === "pinned";
	const standing = trainStanding(train, pickup.candidates, likelyId, pinned, pickup.riderPhase, nowMs);
	const faded = standing === "missed" || standing === "unlikely";
	/*
	 * Once he is parked the only drive left is the one home.
	 *
	 * Sending him to the kerb he is already standing at is the one thing the link cannot usefully
	 * do, and the wait at the kerb is exactly when there is time to start the nav for the trip back.
	 */
	const parked = pickup.driverProgress === "at_pickup_point" || pickup.driverProgress === "picked_up";
	const home = parked ? homeForStation(pickup.stationId) : null;
	const navTarget: NavTarget | null = home
		? { id: home.id, name: home.label, lat: home.lat, lon: home.lon, address: home.address }
		: pickup.stationId
			? { id: pickup.stationId, name: pickup.stationName ?? pickup.stationId, lat: 0, lon: 0 }
			: null;
	const navLabel = home ? `Navigate home to ${home.label}` : `Navigate to ${where}`;
	// Countdowns, because a clock time has to be subtracted from the current time before it says
	// anything. The Rockridge one is deliberately allowed to go negative: "left four minutes ago"
	// is the whole reason that card is marked missed.
	const inMinutes = (ms: number | null) => (ms == null ? null : Math.max(0, Math.round((ms - nowMs) / 60_000)));
	const departIn = train.departureMs == null ? null : Math.round((train.departureMs - nowMs) / 60_000);
	const driveMinutes =
		pickup.driverArrivalMs == null || pickup.driverSeenAtMs == null
			? null
			: Math.max(0, Math.round((pickup.driverArrivalMs - pickup.driverSeenAtMs) / 60_000));
	const arrivedElapsed = pickup.riderArrivedAtMs == null ? null : Math.max(0, nowMs - pickup.riderArrivedAtMs);

	return (
		<article className={`card card--pickup tone-${CARD_TONE[copy.tone] ?? "info"} ${faded ? "is-unlikely" : ""}`}>
			<header className="card__tag">
				<span className="card__tag">Picking up Eben</span>
				<span className={`pill pill--${TRAIN_STANDING_TONES[standing]}`}>{trainStandingLabel(standing, pickup.riderNextStopName)}</span>
			</header>

			<div className="card__hero">
				<p className="card__lead">{copy.lead ?? where}</p>
				<div className="card__valueRow">
					<p className="card__value">
						{copy.hero}
						{copy.unit ? <span className="card__unit">{copy.unit}</span> : null}
					</p>
					{navTarget ? <NavigateLinks target={navTarget} label={navLabel} /> : null}
				</div>
				<div className="card__actionRow">
					<p className="card__action">{copy.sub ?? (pinned ? where : "Train not confirmed yet")}</p>
					{copy.note ? (
						<p className={`card__note card__note--${copy.note.tone}`}>
							<span className="card__noteValue">{copy.note.value}</span> {copy.note.label}
						</p>
					) : null}
				</div>
				{arrivedElapsed != null ? (
					<p className="pickup__elapsed">
						Off train <strong>{elapsedClock(arrivedElapsed)}</strong> ago <span>({arrivalConfidenceLabel(pickup.riderArrivalConfidence)})</span>
					</p>
				) : null}
			</div>

			<dl className="card__facts">
				<div>
					{/* Where he gets on. The one number that says whether he can still make this
					    train — and once he is on it, a spent fact that would only count away from
					    something nobody is deciding any more. */}
					<dt>At Rockridge</dt>
					<dd>
						{standing === "boarded" || standing === "arrived" || departIn == null ? (
							"\u2014"
						) : (
							<>
								{departIn}
								<span className="card__factUnit">min</span>
							</>
						)}
					</dd>
				</div>
				<div>
					<dt>Arrives {where}</dt>
					<dd>
						{inMinutes(train.arrivalMs) ?? "\u2014"}
						<span className="card__factUnit">min</span>
					</dd>
				</div>
				<div>
					{/* Spent once he is standing at the curb, the same as the Rockridge time is once
					    Eben is aboard: a number counting towards something already done. */}
					<dt>Your drive</dt>
					<dd>
						{parked || driveMinutes == null ? (
							"\u2014"
						) : (
							<>
								{driveMinutes}
								<span className="card__factUnit">min</span>
							</>
						)}
					</dd>
				</div>
			</dl>

		</article>
	);
}

function elapsedClock(milliseconds: number): string {
	const seconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(seconds / 60);
	return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function arrivalConfidenceLabel(confidence: PickupState["riderArrivalConfidence"]): string {
	return confidence === "observed" ? "Observed" : confidence === "declared" ? "Declared" : "Estimated";
}

/**
 * The same four trains, as a list.
 *
 * The deck answers "when do I leave for this one"; the board is for scanning them side by side,
 * which is what clock times are good at. Tapping a row is the same decision as swiping the deck.
 */
export function PickupBoard({
	pickup,
	timezone,
	nowMs,
	onChoose,
}: {
	pickup: PickupState;
	timezone: string;
	nowMs: number;
	onChoose: (planId: string) => void;
}) {
	const pinnedId = pickup.stage === "pinned" ? (pickup.train.planId ?? pickup.likelyPlanId) : null;
	const likelyId = pinnedId ?? likelyTrainId(pickup.candidates, pickup.riderMetersToStation, nowMs);
	const deck = pickupDeck(pickup.candidates, likelyId, pinnedId);
	// With one train there is nothing to choose between, so the list would only repeat the card.
	if (deck.length < 2) {
		return null;
	}
	const selectedId = pickup.chosenPlanId ?? likelyId;
	const where = pickup.stationName ? stationLabel(pickup.stationName) : "the station";

	return (
		<section className="board" aria-label="Trains Eben might take">
			<header className="board__head">
				<h2>Trains from Rockridge</h2>
				<span className="board__hint">Tap a train</span>
			</header>

			<ol className="board__list">
				{deck.map((train) => {
					const standing = trainStanding(train, pickup.candidates, likelyId, pinnedId != null, pickup.riderPhase, nowMs);
					const faded = standing === "missed" || standing === "unlikely";
					return (
						<li key={train.planId ?? "train"}>
							<button
								type="button"
								className={`row ${train.planId === selectedId ? "is-selected" : ""} ${faded ? "is-unreachable" : ""}`}
								onClick={() => train.planId && onChoose(train.planId)}
							>
								<span className="row__line" style={{ background: "#3fb8af" }} aria-hidden="true" />
								<Clock ms={train.departureMs} timezone={timezone} className="row__depart" />
								<span className={`pill pill--${TRAIN_STANDING_TONES[standing]} row__tag`}>{trainStandingLabel(standing, pickup.riderNextStopName)}</span>
								<span className="row__final">
									<em>{where}</em>
									<Clock ms={train.arrivalMs} timezone={timezone} className="row__finalTime" />
								</span>
								<span className="row__wait">
									{train.arrivalMs == null ? "\u2014" : `${Math.max(0, Math.round((train.arrivalMs - nowMs) / 60_000))} min`}
								</span>
							</button>
						</li>
					);
				})}
			</ol>
		</section>
	);
}

/**
 * The block on Eben's screen, inside the hero card beneath the facts.
 *
 * No opt-in and no name: "your ride" reads better than "David", and the roles are hardcoded
 * underneath anyway. It disappears entirely a minute after the request if nobody picks it up,
 * so it is never a promise nobody is keeping.
 *
 * The two buttons do not replace the automatic offer, which still raises itself off the fence —
 * he may never open the app, and that is the case this was built for. They are for the afternoons
 * the fence gets wrong: an early finish it has not seen, or a lift he does not want. Both are
 * holds, because the block sits under his thumb for the whole ride.
 */
export function PickupBlock({
	pickup,
	nowMs,
	onRequest,
	onStandDown,
	onRefresh,
	refreshing = false,
	error,
	onCommunicationShown,
}: {
	pickup: PickupState;
	nowMs: number;
	onRequest?: () => void;
	onStandDown?: () => void;
	onRefresh?: () => void;
	refreshing?: boolean;
	error?: string | null;
	onCommunicationShown?: () => void;
}) {
	if (pickup.status === "expired") {
		return null;
	}
	// Standing it down is not the end of the afternoon: he can change his mind, and this is the
	// only place that says so.
	if (pickup.status === "cancelled") {
		return onRequest ? (
			<section className="pickup" data-freshness="live" data-tone="idle">
				<h3 className="pickup__label">Pickup</h3>
				<p className="pickup__status">No lift today</p>
				<HoldButton label="Ask for a ride" actionLabel="Ask for a ride home" className="pickup__ask" onConfirm={onRequest} />
			</section>
		) : null;
	}
	/*
	 * Never a promise nobody is keeping — but being told is not a promise.
	 *
	 * The section used to vanish a minute after the offer if David had not entered. That rule was
	 * about not implying a lift nobody had agreed to, and it also threw away the one thing the
	 * rider actually wanted: confirmation the message arrived. So the grace now only hides the
	 * pickup while *nothing has been delivered*; once a rung has been pushed, it stays and says so.
	 */
	const offered = pickup.status === "offered";
	if (offered && !pickup.communication?.ack && !pickup.communication?.punctuality && pickup.pushedStage == null && pickup.offeredAtMs != null && nowMs - pickup.offeredAtMs > OFFER_GRACE_MS) {
		return null;
	}

	const timing = timingFor(pickup, nowMs);
	const kind = pickup.status === "completed" ? "completed" : offered ? "offered" : pickup.sharing ? "active" : "paused";
	const line = riderPickupLine(kind, pickup.driverProgress, timing, pickup.driverMeters, nowMs, pickup.pushedStage, pickup.riderAtShuttleStop);
	const freshness = freshnessOf(pickup.driverSeenAtMs, nowMs);
	const stale = kind === "active" ? freshnessNote(freshness, pickup.driverSeenAtMs, nowMs) : null;

	return (
		<section className="pickup" data-freshness={kind === "active" ? freshness : "live"} data-tone={line.tone}>
			<header className="pickup__head">
				<h3 className="pickup__label">Pickup</h3>
				{onRefresh ? <RefreshButton className="pickup__refresh" onClick={onRefresh} busy={refreshing} label="Refresh pickup" /> : null}
			</header>
			<p className="pickup__status">{line.status}</p>
			<PickupMessages pickup={pickup} onShown={onCommunicationShown} />
			{error ? <p className="pickup__wait" role="alert">{error}</p> : null}
			{/* The wait and how old the reading is are one thought, so they share a line. */}
			{line.wait || stale ? (
				<p className="pickup__wait">
					{line.wait}
					{stale ? <span className="pickup__stale">{line.wait ? ` · ${stale}` : stale}</span> : null}
				</p>
			) : null}
			{onStandDown && pickup.status !== "completed" ? (
				<HoldButton
					label="Not today"
					actionLabel="Call off today's pickup"
					className="pickup__ask pickup__ask--off"
					holdMs={HOLD_DESTRUCTIVE_MS}
					onConfirm={onStandDown}
				/>
			) : null}
		</section>
	);
}

/**
 * The way in and the way out, in the slot the next step already owns.
 *
 * Entering is the big pill, because when Eben is moving it is the one thing the app is for.
 * Once it is open, cancel ends the pickup for today and leave only pauses sharing, which is why
 * cancel is the one that has to be held.
 */
export function PickupButton({
	pickup,
	nowMs,
	onEnter,
	onExit,
	onCancel,
	onDriving,
	onArrive,
	onComplete,
}: {
	pickup: PickupState;
	nowMs: number;
	onEnter: () => void;
	onExit: () => void;
	onCancel: () => void;
	onDriving: () => void;
	onArrive: () => void;
	onComplete: () => void;
}) {
	if (pickup.role !== "driver" || pickup.status === "cancelled" || pickup.status === "expired" || pickup.status === "completed") {
		return null;
	}
	const inside = pickup.sharing && pickup.status === "active";
	const timing = timingFor(pickup, nowMs);
	const suffix = inside ? null : entrySuffix(pickup, timing, nowMs);
	const entryLabel = pickup.status === "offered" && pickup.driverProgress === "not_started" ? "Start pickup" : "Back to pickup";
	const label = suffix ? `${entryLabel} - ${suffix}` : entryLabel;
	const hasProgressAction = inside && ["preparing", "driving", "at_pickup_point"].includes(pickup.driverProgress);

	return (
		<div className={`nextstep ${inside ? "nextstep--pickup-actions" : ""} ${hasProgressAction ? "nextstep--pickup-actions-expanded" : ""}`}>
			{[0, 1, 2, 3].map((layer) => (
				<div key={layer} className="nextstep__haze" data-layer={layer} aria-hidden="true" />
			))}
			{inside ? (
				<>
					{pickup.driverProgress === "preparing" ? (
						<HoldButton
							label="On my way"
							actionLabel="On my way"
							onConfirm={onDriving}
							className="nextstep__button nextstep__button--pickup-progress"
							phase="driving"
						/>
					) : pickup.driverProgress === "driving" ? (
						<div className="nextstep__pickup-progress">
						<HoldButton
							label="I’m here"
							actionLabel="Confirm that you are here"
							onConfirm={onArrive}
							holdMs={HOLD_DESTRUCTIVE_MS}
							className="nextstep__button"
							phase="at_destination"
						/>
						{canConfirmPickup(pickup.riderPhase, pickup.driverProgress) ? (
							<HoldButton label="Got him" actionLabel="Confirm pickup" onConfirm={onComplete} className="nextstep__button" phase="arrived" />
						) : null}
						</div>
					) : pickup.driverProgress === "at_pickup_point" ? (
						<HoldButton
							label="Got him"
							actionLabel="Confirm pickup"
							onConfirm={onComplete}
							className="nextstep__button nextstep__button--pickup-progress"
							phase="arrived"
						/>
					) : null}
					<HoldButton
						label="Cancel"
						actionLabel="Cancel today's pickup"
						onConfirm={onCancel}
						holdMs={HOLD_DESTRUCTIVE_MS}
						className="nextstep__button nextstep__button--cancel"
						phase="pickup-cancel"
					/>
					<HoldButton
						label="Leave pickup mode"
						actionLabel="Leave pickup mode"
						onConfirm={onExit}
						holdMs={HOLD_DESTRUCTIVE_MS}
						className="nextstep__button nextstep__button--leave"
						phase="pickup-leave"
					/>
				</>
			) : (
				<HoldButton label={label} actionLabel={label} phase="pickup" className="nextstep__button" onConfirm={onEnter} />
			)}
		</div>
	);
}

/**
 * Enough of the answer to decide whether to tap.
 *
 * When to set off, once there is a drive to count back through. Before that there is no route
 * yet — his phone has not shared a position — so it falls back to when the train gets in, which
 * is the other half of the same question.
 */
function entrySuffix(pickup: PickupState, timing: ReturnType<typeof timingFor>, nowMs: number): string | null {
	if (pickup.driverProgress === "at_pickup_point") {
		return "parked at pickup";
	}
	if (pickup.riderPhase !== "on_train" && pickup.riderPhase !== "at_destination" && pickup.riderPhase !== "arrived") {
		return `Eben ${PICKUP_RIDER_STEPS[riderStepIndex(pickup.riderPhase, pickup.stage)].toLowerCase()}`;
	}
	if (timing.startByMs != null) {
		return nowMs >= timing.startByMs ? "leave now" : `leave in ${Math.max(0, Math.round((timing.startByMs - nowMs) / 60_000))} min`;
	}
	if (pickup.train.arrivalMs != null) {
		return trainRelativeText(pickup.train.arrivalMs, nowMs);
	}
	return deltaText(timing.deltaSeconds);
}
